import { useEffect, useMemo, useState } from 'react'
import type { Category, Item, Member, Recipe, RecipeSource } from '../../lib/supabase'
import {
  allTags, combineNeeds, coverModel, ingredientCategory, missingCount, passesFilter, recipeSource, recipeSources, visibleRecipes,
  SOURCE_LABELS, type Need, type RecipeFilter,
} from '../../lib/recipeBook'
import type { Choice } from '../../lib/choices'
import { checkRecipeLink, linkInText, postPlatform } from '../../lib/recipeImport'
import type { ImportError, ImportOutcome, RecipeElsewhere, RecipePatch } from '../../hooks/useRecipes'
import { useLayout } from '../../hooks/useLayout'
import { Hero } from '../list/Hero'
import { SidebarSlot } from '../shell/AppShell'
import { ActionBar } from '../ActionBar'
import { ChoicesSheet } from '../choices/ChoicesSheet'
import { knownNames } from '../../lib/catalog'
import { indexItems } from '../../lib/search'
import { reviewRows } from '../../lib/importReview'
import { ImportReviewSheet, type ReviewRow } from './ImportReviewSheet'
import { ClipboardIcon, CloseIcon, LinkIcon, PencilIcon, PlusIcon, SearchIcon } from '../icons'
import { AnimatePresence } from 'framer-motion'
import { SheetPanel } from '../SheetPanel'
import { RecipeCard } from './RecipeCard'
import { SearchField } from '../ui/SearchField'
import { ImportHelp } from './ImportHelp'

export interface RecipesTabProps {
  recipes: Recipe[]
  items: Item[]
  members: Member[]
  categories: Category[]
  locked: boolean
  /** The household's own guess for where an ingredient goes (resolveCategoryId). */
  categoryFor: (name: string) => string | null
  /** The household's own name for an ingredient (useItemHistory), so a recipe never adds a second spelling. */
  spellingFor?: (name: string) => string
  /** Names bought before, suggested when a row of the choices sheet is edited (lib/catalog.ts). */
  before?: { name: string }[]
  tintFor: (categoryId: string | null) => string
  /** Imports a link, naming its ingredients with `nameFor`; an Instagram post with its pasted caption (useRecipes). */
  importRecipe: (url: string, nameFor?: (name: string) => string, caption?: string) => Promise<ImportOutcome>
  /** Imports a recipe's pasted text (copied out of a screenshot with Live Text, say) as a new recipe (useRecipes). */
  importText?: (text: string, nameFor?: (name: string) => string) => Promise<ImportOutcome>
  /** Opens the recipe editor on a saved recipe: a video imported without ingredients. */
  /** Opens the editor; `sourceText` is a page's text, for a recipe it was read from only in part. */
  onEditRecipe?: (id: string, sourceText?: string) => void
  /** A link shared into the app (Android share sheet, ?import=); imported once, then `onSharedLinkUsed`. */
  sharedLink?: string | null
  /** Home's recipe tile: open Add a recipe, and import `text` (pasted on Home) straight away. */
  addRequest?: { text?: string } | null
  onAddRequestUsed?: () => void
  onSharedLinkUsed?: () => void
  updateRecipe: (id: string, patch: RecipePatch) => void
  onOpen: (id: string) => void
  /** Opens the recipe editor on a new, hand-written recipe. */
  onNewRecipe: () => void
  /** recipe id → its photo's signed URL (useRecipePhotos). */
  photoUrls?: Map<string, string>
  /** Add to list from the choices sheet (useApplyChoices). */
  onApply: (needs: Need[], choices: Choice[], note: string) => void
  onUpgrade: () => void
}

const FILTERS: { value: RecipeFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'starred', label: 'Starred' },
  { value: 'quick', label: 'Under 30 min' },
  { value: 'veg', label: 'Vegetarian' },
  { value: 'rated', label: '4+ stars' },
]

// What went wrong, and what to do instead: there is always a next step.
const IMPORT_ERRORS: Record<Exclude<ImportError, 'subscription_required' | 'needs_caption'>, string> = {
  no_recipe: "We couldn't find a recipe on that page. Copy the recipe there and tap Paste recipe text.",
  text_no_recipe: "We couldn't find ingredients in that text. Copy the ingredient lines too.",
  fetch_failed: "That site didn't answer, or doesn't let apps read it. Copy the recipe there and tap Paste recipe text.",
  rate_limited: 'Lots of imports just now. Try again in a minute.',
  failed: 'Something went wrong. Check your connection and try again.',
  source_blocked: 'This site has asked not to be imported. You can write the recipe yourself instead.',
}

/** A post whose recipe has to be pasted: where it is, and on which platform. */
function captionAsk(where: RecipeElsewhere | undefined, platform: string | null): string {
  if (where === 'comments') return "The recipe is in the post's comments. Copy it there and paste it below."
  if (where === 'bio') return 'The recipe is behind the link in the bio. Copy it there and paste it below, or import that link.'
  if (platform === 'TikTok') return "TikTok cut this post's caption short. Copy it there and paste it below."
  return "Instagram didn't share this post's caption. Copy it there and paste it below."
}

// The Recipes tab (feature spec 02): import from a link, search, filter by the
// built-ins and the household's tags, the grid, and ticking several recipes to
// shop for them at once through the bottom bar and the choices sheet.
export function RecipesTab(p: RecipesTabProps) {
  const layout = useLayout()
  const desktop = layout === 'desktop'
  const [link, setLink] = useState('')
  const [linkError, setLinkError] = useState<string | null>(null)
  const [importing, setImporting] = useState<string | null>(null)
  // A clean import's one line: nothing to ask, so it says what it did.
  const [imported, setImported] = useState<string | null>(null)
  const [fresh, setFresh] = useState<Set<string>>(new Set())
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<RecipeFilter>('all')
  const [tag, setTag] = useState<string | null>(null)
  const [source, setSource] = useState<RecipeSource | null>(null)
  const [selected, setSelected] = useState<string[]>([])
  const [shopping, setShopping] = useState(false)
  // A post's caption, pasted — asked for only when the import couldn't read the
  // recipe off the post (Instagram's login wall, a TikTok caption cut short, a
  // recipe in the comments or bio), and only for that link.
  const [caption, setCaption] = useState('')
  const [captionFor, setCaptionFor] = useState<{ url: string; where?: RecipeElsewhere } | null>(null)
  // A recipe's text, pasted: open once asked for ("Paste recipe text").
  const [recipeText, setRecipeText] = useState<string | null>(null)
  const [review, setReview] = useState<{ id: string; title: string; rows: ReviewRow[] } | null>(null)
  // Add a recipe (link, text, write your own) is a sheet behind the (+), so the
  // tab starts with the recipes; on the phone search waits behind its magnifier.
  const [adding, setAdding] = useState(false)
  const [searching, setSearching] = useState(false)
  const known = useMemo(() => knownNames(p.items, p.before ?? []), [p.items, p.before])

  const tags = useMemo(() => allTags(p.recipes), [p.recipes])
  const shown = useMemo(() => visibleRecipes(p.recipes, filter, tag, query, source), [p.recipes, filter, tag, query, source])
  // A source chip only says something once recipes come from more than one place.
  const sources = useMemo(() => recipeSources(p.recipes), [p.recipes])
  const sourceChips = sources.length > 1 ? sources : []
  const chosen = p.recipes.filter(r => selected.includes(r.id))
  const needs = useMemo(() => combineNeeds(chosen.map(recipe => ({ recipe, servings: null })), p.spellingFor), [chosen, p.spellingFor])
  const shoppingCount = needs.filter(n => !n.pantry).length
  const starred = p.recipes.filter(r => r.starred).length

  const guard = (fn: () => void) => () => (p.locked ? p.onUpgrade() : fn())

  const typed = checkRecipeLink(link)
  const askCaption = typed.ok && captionFor?.url === typed.url

  async function runImport(raw: string = link) {
    const check = checkRecipeLink(raw)
    if (raw !== link) setLink(raw)
    if (!check.ok) { setLinkError(check.error); return }
    if (p.locked) { p.onUpgrade(); return }
    setLinkError(null)
    setImported(null)
    setImporting(check.host)
    const pasted = captionFor?.url === check.url && caption.trim() ? caption : undefined
    const outcome = await p.importRecipe(check.url, p.spellingFor, pasted)
    setImporting(null)
    if (outcome.ok) {
      setLink('')
      setCaption('')
      setCaptionFor(null)
      saved(outcome)
      return
    }
    if (outcome.error === 'subscription_required') { p.onUpgrade(); return }
    if (outcome.error === 'needs_caption') {
      setCaptionFor({ url: check.url, where: outcome.where })
      setLinkError(captionAsk(outcome.where, postPlatform(check.url)))
      return
    }
    setLinkError(IMPORT_ERRORS[outcome.error])
  }

  // A recipe's text: from the box, or from Paste when the clipboard holds no link.
  async function runTextImport(text: string) {
    if (!p.importText || !text.trim()) return
    if (p.locked) { p.onUpgrade(); return }
    setLinkError(null)
    setImported(null)
    setImporting('your text')
    const outcome = await p.importText(text, p.spellingFor)
    setImporting(null)
    if (outcome.ok) {
      setRecipeText(null)
      saved(outcome)
      return
    }
    if (outcome.error === 'subscription_required') { p.onUpgrade(); return }
    setLinkError(outcome.error === 'needs_caption' ? IMPORT_ERRORS.failed : IMPORT_ERRORS[outcome.error])
  }

  // What follows a saved import, from a link or from text.
  function saved(outcome: Extract<ImportOutcome, { ok: true }>) {
    setAdding(false)
    setFresh(f => new Set(f).add(outcome.id))
    // A video, text or page that didn't list its ingredients: straight to the
    // editor to add them, a page's text beside it to copy from.
    if (outcome.ingredients.length === 0) { p.onEditRecipe?.(outcome.id, outcome.sourceText); return }
    // The import filled in what it could; ask only about the rest (lib/importReview.ts).
    const rows = reviewRows(outcome.ingredients, outcome.renamed, {
      known: indexItems(known),
      categoryOf: g => ingredientCategory(g, p.categoryFor, p.categories),
    })
    if (rows.length) setReview({ id: outcome.id, title: outcome.title, rows })
    else {
      const n = outcome.ingredients.length
      setImported(`Saved “${outcome.title}”: ${n === 1 ? 'one ingredient' : `${n} ingredients`}, each in its aisle.`)
    }
  }

  // A link copied in another app: one tap reads it (iPhone asks to allow the
  // paste). No link but a few lines — a recipe copied out of a screenshot with
  // Live Text — is read as the recipe's text.
  async function pasteLink() {
    let text = ''
    try { text = await navigator.clipboard.readText() } catch { /* denied or unsupported */ }
    importPasted(text)
  }
  function importPasted(text: string) {
    const found = linkInText(text)
    if (found) { void runImport(found); return }
    if (p.importText && text.split('\n').filter(l => l.trim()).length >= 2) { void runTextImport(text); return }
    setLinkError('There is no link on the clipboard. Copy the link in the other app first.')
  }

  // A link shared into the app: imported as soon as the tab shows it.
  const { sharedLink, onSharedLinkUsed } = p
  useEffect(() => {
    if (!sharedLink) return
    onSharedLinkUsed?.()
    // After this render, as if the member had opened Add a recipe, pasted it and tapped Import.
    queueMicrotask(() => { setAdding(true); void runImport(sharedLink) })
  }, [sharedLink]) // eslint-disable-line react-hooks/exhaustive-deps

  // Home's tile: the sheet, and what Home's Paste read, imported as if pasted here.
  const { addRequest, onAddRequestUsed } = p
  useEffect(() => {
    if (!addRequest) return
    onAddRequestUsed?.()
    const text = addRequest.text
    queueMicrotask(() => { setAdding(true); if (text != null) importPasted(text) })
  }, [addRequest]) // eslint-disable-line react-hooks/exhaustive-deps

  function chooseFilter(f: RecipeFilter) {
    setFilter(f)
    if (f === 'all') setTag(null)
  }
  const showAll = () => { setFilter('all'); setTag(null); setSource(null); setQuery('') }

  // 4+ stars only once a recipe has a page's rating to go on.
  const filterButtons = FILTERS.filter(f => f.value !== 'rated' || p.recipes.some(r => r.rating_value != null)).map(f => (
    <button key={f.value} type="button" aria-pressed={filter === f.value}
      className={`ml-filter${filter === f.value ? ' is-selected' : ''}`} onClick={() => chooseFilter(f.value)}>
      {f.label}{' '}<span className="ml-filter__count ml-num">{p.recipes.filter(r => passesFilter(r, f.value)).length}</span>
    </button>
  ))
  const sourceButtons = sourceChips.map(s => (
    <button key={s.source} type="button" aria-pressed={source === s.source}
      className={`ml-filter ml-filter--tag${source === s.source ? ' is-selected' : ''}`}
      onClick={() => setSource(cur => (cur === s.source ? null : s.source))}>
      {SOURCE_LABELS[s.source]}{' '}<span className="ml-filter__count ml-num">{s.count}</span>
    </button>
  ))
  const tagButtons = tags.map(t => (
    <button key={t.name} type="button" aria-pressed={tag === t.name}
      className={`ml-filter ml-filter--tag${tag === t.name ? ' is-selected' : ''}`}
      onClick={() => setTag(cur => (cur === t.name ? null : t.name))}>
      {t.name}{' '}<span className="ml-filter__count ml-num">{t.count}</span>
    </button>
  ))

  const hero = (
    <Hero count={p.recipes.length} label="Recipes saved" layout={layout}>
      {desktop && <span className="ml-label">{starred} starred</span>}
    </Hero>
  )

  const empty = shown.length === 0 && p.recipes.length > 0 && (
    <div className="ml-empty">
      <p className="ml-label ml-empty__title">
        {query.trim() ? 'No recipes match' : filter === 'starred' && !tag ? 'No starred recipes yet' : tag ? `Nothing tagged ${tag} here` : 'No recipes match'}
      </p>
      <p className="ml-caption">
        {filter === 'starred' && !tag && !query.trim() ? 'Star the ones you make often to find them here.' : 'Try another word or filter.'}
      </p>
      <button type="button" className="ml-btn ml-btn--secondary" onClick={showAll}>Show all recipes</button>
    </div>
  )

  return (
    <div className={desktop ? 'ml-tab ml-tab--desktop ml-recipes' : 'ml-tab ml-recipes'}>
      {desktop ? (
        <SidebarSlot>
          {hero}
          <SearchField label="Search recipes" value={query} onChange={setQuery} />
          <div className="ml-sidebar-filters" role="group" aria-label="Filters">
            <h2 className="ml-label ml-section-label">Filters</h2>
            {filterButtons}
          </div>
          {sourceChips.length > 0 && (
            <div className="ml-sidebar-filters" role="group" aria-label="Source">
              <h2 className="ml-label ml-section-label">Source</h2>
              {sourceButtons}
            </div>
          )}
          {tags.length > 0 && (
            <div className="ml-sidebar-filters" role="group" aria-label="Tags">
              <h2 className="ml-label ml-section-label">Tags</h2>
              {tagButtons}
            </div>
          )}
        </SidebarSlot>
      ) : null}

      {desktop && (
        <header className="ml-plan__header">
          <h1 className="ml-plan__title ml-display">Recipes</h1>
          <button type="button" className="ml-btn ml-btn--primary ml-recipes__add-wide" onClick={() => setAdding(true)}>
            <PlusIcon size={18} /> Add a recipe
          </button>
        </header>
      )}

      {!desktop && (searching ? (
        <div className="ml-recipes__bar">
          <SearchField className="ml-recipes__search" label="Search recipes, ingredients or tags" value={query} onChange={setQuery} autoFocus />
          <button type="button" className="ml-round-btn" aria-label="Close search"
            onClick={() => { setSearching(false); setQuery('') }}><CloseIcon size={20} /></button>
        </div>
      ) : (
        <div className="ml-recipes__bar">
          <div className="ml-filters ml-recipes__filters" role="group" aria-label="Filters">
            {filterButtons}
            {sourceChips.length > 0 && <span className="ml-filters__divider" aria-hidden="true" />}
            {sourceButtons}
            {tags.length > 0 && <span className="ml-filters__divider" aria-hidden="true" />}
            {tagButtons}
          </div>
          <button type="button" className="ml-round-btn" aria-label="Search" onClick={() => setSearching(true)}>
            <SearchIcon size={20} />
          </button>
          <button type="button" className="ml-round-btn ml-round-btn--primary" aria-label="Add a recipe" onClick={() => setAdding(true)}>
            <PlusIcon size={20} />
          </button>
        </div>
      ))}

      {imported && <p className="ml-caption ml-import__done" role="status">{imported}</p>}

      <AnimatePresence>
        {adding && (
          <SheetPanel onClose={() => setAdding(false)} ariaLabel="Add a recipe" maxHeight="92dvh" layout="scroll">
            <h2 className="ml-sheet-title">Add a recipe</h2>
        <section className="ml-import" aria-label="Import a recipe">
          <ImportHelp>
            <div className="ml-import__field">
              <LinkIcon size={20} />
              <input className="ml-import__input" type="url" inputMode="url" value={link}
                placeholder="Recipe or video link" aria-label="Paste a link to a recipe page or a video"
                aria-invalid={linkError ? true : undefined} aria-describedby={linkError ? 'ml-import-error' : undefined}
                onChange={e => { setLink(e.target.value); setLinkError(null) }}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); runImport() } }} />
              {link.trim()
                ? <button type="button" className="ml-btn ml-btn--primary ml-import__btn" disabled={!!importing} onClick={() => runImport()}>Import</button>
                : <button type="button" className="ml-btn ml-btn--secondary ml-import__btn" disabled={!!importing} onClick={pasteLink}>Paste</button>}
            </div>
          </ImportHelp>
          {askCaption && (
            <label className="ml-field ml-import__caption">
              <span className="ml-label">{captionFor?.where ? 'The recipe, copied' : `${postPlatform(captionFor?.url ?? '') ?? 'Post'} caption`}</span>
              <textarea className="ml-input" rows={5} value={caption} onChange={e => { setCaption(e.target.value); setLinkError(null) }}
                placeholder={captionFor?.where ? 'The ingredients and the method' : "The post's caption, with its ingredients"} />
            </label>
          )}
          {recipeText != null && (
            <div className="ml-import__caption">
              <label className="ml-field">
                <span className="ml-label">Recipe text</span>
                <textarea className="ml-input" rows={6} value={recipeText} onChange={e => { setRecipeText(e.target.value); setLinkError(null) }}
                  placeholder="The title, the ingredients and the method, as copied" />
              </label>
              <button type="button" className="ml-btn ml-btn--primary" disabled={!!importing || !recipeText.trim()}
                onClick={() => runTextImport(recipeText)}>Import text</button>
            </div>
          )}
          {linkError && <p id="ml-import-error" className="ml-import__error" role="alert">{linkError}</p>}
          {importing && <p className="ml-caption ml-import__done">Importing from {importing}…</p>}
          {p.importText && recipeText == null && (
            <button type="button" className="ml-text-btn ml-import__write" onClick={guard(() => setRecipeText(''))}>
              <ClipboardIcon size={16} /> Paste recipe text
            </button>
          )}
          <button type="button" className="ml-text-btn ml-import__write" onClick={guard(() => { setAdding(false); p.onNewRecipe() })}>
            <PencilIcon size={16} /> Or write your own recipe
          </button>
        </section>
          </SheetPanel>
        )}
      </AnimatePresence>

      {empty}
      <ul className="ml-recipe-grid">
        {importing && (
          <li className="ml-recipe-cell">
            <div className="ml-recipe-placeholder" role="status">
              <span>Importing from {importing}</span>
              <span className="ml-recipe-placeholder__line" />
              <span className="ml-recipe-placeholder__line ml-recipe-placeholder__line--short" />
            </div>
          </li>
        )}
        {shown.map(r => (
          <RecipeCard key={r.id} recipe={r}
            cover={coverModel(r, p.categoryFor, p.categories)}
            photoUrl={p.photoUrls?.get(r.id)}
            source={recipeSource(r, p.members)}
            missing={missingCount(r, p.items)}
            selected={selected.includes(r.id)}
            isNew={fresh.has(r.id)}
            tintFor={p.tintFor}
            onToggleSelect={() => setSelected(s => (s.includes(r.id) ? s.filter(x => x !== r.id) : [...s, r.id]))}
            onToggleStar={guard(() => p.updateRecipe(r.id, { starred: !r.starred }))}
            onOpen={() => { setFresh(f => { const n = new Set(f); n.delete(r.id); return n }); p.onOpen(r.id) }} />
        ))}
      </ul>

      <ActionBar
        selected={chosen.length ? {
          title: `${chosen.length} ${chosen.length === 1 ? 'recipe' : 'recipes'} selected`,
          subtitle: `${shoppingCount} ${shoppingCount === 1 ? 'ingredient' : 'ingredients'}`,
        } : null}
        onAdd={guard(() => setShopping(true))}
        onClear={() => setSelected([])} />

      <ChoicesSheet before={p.before} open={shopping} kicker="Shopping for"
        title={`${chosen.length} ${chosen.length === 1 ? 'recipe' : 'recipes'}`}
        chips={chosen.map(r => r.title)} needs={needs} items={p.items} source="recipes"
        onApply={(edited, choices) => {
          p.onApply(edited, choices, chosen.map(r => r.title).join(', '))
          setShopping(false)
          setSelected([])
        }}
        onClose={() => setShopping(false)} />

      <ImportReviewSheet review={review} known={known} categories={p.categories} tintFor={p.tintFor} onClose={() => setReview(null)}
        onSave={changes => {
          const recipe = review && p.recipes.find(r => r.id === review.id)
          if (!recipe) return
          p.updateRecipe(recipe.id, { ingredients: recipe.ingredients.map((g, i) => (changes.has(i) ? { ...g, ...changes.get(i) } : g)) })
        }} />
    </div>
  )
}
